<template>
  <div class="notification-bell">
    <el-popover
      v-model="visible"
      trigger="click"
      placement="bottom-end"
      :width="420"
      :visible-arrow="false"
      popper-class="portal-header-popover"
      @show="$emit('open')"
      @hide="$emit('close')"
      @after-enter="focusPanel"
    >
      <section
        :id="panelId"
        ref="panel"
        tabindex="-1"
        aria-label="Notificaciones"
        @keydown.esc.stop.prevent="close(true)"
      >
        <div class="notification-bell__header">
          <div>
            <h6 class="notification-bell__title">Notificaciones</h6>
            <p class="notification-bell__subtitle">Avisos y novedades</p>
          </div>
          <div class="d-flex align-items-center gap-2">
            <el-button
              type="text"
              icon="el-icon-refresh"
              class="icono-accion"
              :loading="loading"
              aria-label="Actualizar notificaciones"
              title="Actualizar notificaciones"
              @click="$emit('refresh')"
            />
            <el-button
              type="text"
              icon="el-icon-close"
              class="m-0"
              aria-label="Cerrar notificaciones"
              @click="close(true)"
            />
          </div>
        </div>
        <div class="notification-bell__body" :aria-busy="loading ? 'true' : 'false'">
          <p v-if="error" class="notification-bell__error" role="alert">
            <i class="el-icon-warning-outline" aria-hidden="true"></i>
            {{ error }}
          </p>
          <div v-if="loading && !count" class="p-4">
            <el-skeleton animated :rows="4" />
          </div>
          <NotificationList
            v-else-if="count"
            :notifications="notifications"
            :downloading-id="downloadingId"
            @download="$emit('download', $event)"
          />
          <div v-else-if="!error" class="notification-bell__empty">
            <span class="notification-bell__empty-icon" aria-hidden="true">
              <i class="el-icon-check"></i>
            </span>
            <p class="notification-bell__empty-title">Estás al día</p>
            <p class="mb-0 text-muted">No hay notificaciones disponibles.</p>
          </div>
        </div>
        <div v-if="count" class="notification-bell__footer">
          {{ count }} {{ count === 1 ? 'notificación disponible' : 'notificaciones disponibles' }}
        </div>
      </section>
      <button
        slot="reference"
        ref="trigger"
        type="button"
        class="notification-bell__trigger portal-header-action"
        :class="{ 'notification-bell__trigger--active': visible }"
        :aria-label="count ? `Notificaciones: ${count} disponibles` : 'Notificaciones'"
        :aria-expanded="visible ? 'true' : 'false'"
        :aria-controls="panelId"
        :aria-busy="loading ? 'true' : 'false'"
        @keydown.esc.stop.prevent="close(true)"
      >
        <i
          class="el-icon-bell notification-bell__icon"
          :class="{ 'notification-bell__icon--ring': announcing }"
          aria-hidden="true"
          @animationend="announcing = false"
        ></i>
        <span v-if="count" class="notification-bell__count" aria-hidden="true">
          {{ count > 99 ? '99+' : count }}
        </span>
        <i
          v-if="loading"
          class="el-icon-loading notification-bell__loading"
          aria-hidden="true"
        ></i>
      </button>
    </el-popover>
    <span class="visually-hidden" role="status" aria-live="polite">
      {{ loading ? 'Cargando notificaciones' : `${count} notificaciones disponibles` }}
    </span>
  </div>
</template>

<script>
import NotificationList from "./NotificationList.vue";

export default {
  name: "NotificationBell",
  components: { NotificationList },
  props: {
    notifications: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    error: { type: String, default: "" },
    downloadingId: { type: String, default: null },
  },
  data() {
    return { visible: false, announcing: false, panelId: `notification-panel-${this._uid}` };
  },
  computed: {
    count() {
      return this.notifications.length;
    },
  },
  watch: {
    count: {
      immediate: true,
      handler(value, previous) {
        if (value > 0 && value !== previous) {
          this.announcing = false;
          this.$nextTick(() => {
            if (!this._isDestroyed) this.announcing = true;
          });
        }
      },
    },
  },
  methods: {
    close(restoreFocus = false) {
      this.visible = false;
      if (restoreFocus && this.$refs.trigger) this.$refs.trigger.focus();
    },
    focusPanel() {
      if (this.visible && this.$refs.panel) this.$refs.panel.focus();
    },
  },
};
</script>

<style lang="scss" scoped>
.notification-bell {
  display: inline-flex;
  align-items: center;
}

.notification-bell__icon {
  font-size: 25px;
  transform-origin: 50% 15%;

  &--ring {
    animation: notification-bell-ring 0.8s ease-in-out;
  }
}

.notification-bell__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px 14px;
}

.notification-bell__title {
  margin: 0;
  color: #303133;
  font-size: 16px;
  text-transform: none;
}

.notification-bell__subtitle {
  margin: 4px 0 0;
  color: #909399;
  font-size: 12px;
}

.notification-bell__body {
  max-height: min(420px, calc(100vh - 190px));
  overflow-y: auto;
  overscroll-behavior: contain;
}

.notification-bell__error {
  margin: 8px 20px 20px;
  color: #b7791f;
  font-size: 13px;
  line-height: 1.6;
}

.notification-bell__empty {
  padding: 32px 20px 40px;
  text-align: center;
  font-size: 13px;
}

.notification-bell__empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  margin-bottom: 16px;
  border-radius: 18px;
  color: #447df7;
  background: #edf3ff;
  font-size: 26px;
}

.notification-bell__empty-title {
  margin: 0 0 6px;
  color: #303133;
  font-size: 15px;
  font-weight: 600;
}

.notification-bell__footer {
  padding: 12px 20px;
  color: #909399;
  background: #f7f9fc;
  font-size: 12px;
}

@keyframes notification-bell-ring {
  0%, 100% { transform: rotate(0); }
  20%, 60% { transform: rotate(14deg); }
  40%, 80% { transform: rotate(-10deg); }
}

@media (prefers-reduced-motion: reduce) {
  .notification-bell__trigger { transition: none; }
  .notification-bell__icon--ring { animation: none; }
}
</style>

